package com.shadowings.gero.presentation.component.quiz

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.pager.PagerState
import androidx.compose.foundation.pager.rememberPagerState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Button
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedCard
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.MutableState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.semantics.testTag
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import androidx.navigation.NavController
import androidx.navigation.compose.rememberNavController
import com.shadowings.gero.domain.model.IQuizContent
import com.shadowings.gero.domain.model.QuizQuestionType
import com.shadowings.gero.domain.model.QuizText
import com.shadowings.gero.domain.model.TrueFalseContent
import com.shadowings.gero.domain.model.UserData
import com.shadowings.gero.domain.model.WrongAnswerRecord
import com.shadowings.gero.presentation.ui.AppTheme
import com.shadowings.gero.presentation.ui.quizSelectionCorrectBg
import com.shadowings.gero.presentation.ui.quizSelectionPendingBg
import com.shadowings.gero.presentation.ui.quizSelectionWrongBg
import com.shadowings.gero.presentation.ui.testModeCorrect
import androidx.compose.ui.hapticfeedback.HapticFeedbackType
import androidx.compose.ui.platform.LocalHapticFeedback
import com.shadowings.gero.presentation.util.DeviceClass
import com.shadowings.gero.presentation.util.DevicePreviews
import com.shadowings.gero.presentation.util.rememberDeviceInfo
import gero.composeapp.generated.resources.Res
import gero.composeapp.generated.resources.false_option
import gero.composeapp.generated.resources.quiz_show_options_button
import gero.composeapp.generated.resources.true_option
import org.jetbrains.compose.resources.stringResource
import gero.composeapp.generated.resources.share_quiz_true_false

@DevicePreviews
@Preview
@Preview(name = "Dark", uiMode = 0x20, showBackground = true)
@Composable
private fun TrueFalseContentComposablePreview() {
    AppTheme {
        Surface {
            TrueFalseContentComposable(
                quiz = TrueFalseContent(
                    id = "preview",
                    text = QuizText.Plain("I geroglifici erano usati dagli antichi egizi per fare la salamoia."),
                    correct = true
                ),
                comboCount = mutableStateOf(0),
                quizList = mutableStateOf(listOf()),
                page = 0,
                pagerState = rememberPagerState { 1 },
                navController = rememberNavController(),
                quizResults = mutableStateOf(mapOf()),
                errorsCount = mutableStateOf(0),
                testMode = false,
                userData = UserData(),
            )
        }
    }
}


@Composable
fun TrueFalseContentComposable(
    quiz: TrueFalseContent,
    comboCount: MutableState<Int>,
    quizList: MutableState<List<IQuizContent>>,
    page: Int,
    pagerState: PagerState,
    navController: NavController,
    quizResults: MutableState<Map<String, Boolean>>,
    errorsCount: MutableState<Int>,
    testMode: Boolean,
    userData: UserData,
    lessonId: String = "",
    hideOptions: Boolean = false,
    onComboFiveReached: (() -> Unit)? = null,
    onNextQuestion: ((isCorrect: Boolean) -> Unit)? = null,
    onAnswerResult: ((isCorrect: Boolean, record: WrongAnswerRecord) -> Unit)? = null
) {
    val state = rememberQuizPageState(
        quizId = quiz.id,
        page = page,
        pagerState = pagerState,
        navController = navController,
        quizResults = quizResults,
        quizList = quizList,
        comboCount = comboCount,
        errorsCount = errorsCount,
        onComboFiveReached = onComboFiveReached,
        onNextQuestion = onNextQuestion,
    )
    val hapticFeedback = LocalHapticFeedback.current
    val deviceInfo = rememberDeviceInfo()
    val trueLabel = stringResource(Res.string.true_option)
    val falseLabel = stringResource(Res.string.false_option)
    var trueSelected by rememberSaveable { mutableStateOf(false) }
    var falseSelected by rememberSaveable { mutableStateOf(false) }
    var revealed by rememberSaveable(quiz.id) { mutableStateOf(false) }

    val isLandscape = deviceInfo.isLandscape

    Box(modifier = Modifier.fillMaxSize()) {
        run {
            val questionContent = @Composable {
                QuestionBox(
                    question = quiz.text.resolve(),
                    questionTestTag = "true_false_question"
                )
            }

            val trueBg = when {
                state.blinking -> MaterialTheme.colorScheme.surfaceVariant
                trueSelected && !state.showResult -> quizSelectionPendingBg
                state.showResult && quiz.correct -> quizSelectionCorrectBg
                trueSelected && state.showResult && !quiz.correct -> quizSelectionWrongBg
                else -> MaterialTheme.colorScheme.surfaceVariant
            }
            val falseBg = when {
                state.blinking -> MaterialTheme.colorScheme.surfaceVariant
                falseSelected && !state.showResult -> quizSelectionPendingBg
                falseSelected && state.showResult && quiz.correct -> quizSelectionWrongBg
                state.showResult && !quiz.correct -> quizSelectionCorrectBg
                else -> MaterialTheme.colorScheme.surfaceVariant
            }

            val buttonsContent = @Composable {
                if (hideOptions && !revealed) {
                    RevealOptionsButton(onClick = { revealed = true })
                } else {
                OutlinedCard(
                    onClick = {
                        if (state.pending) {
                            hapticFeedback.performHapticFeedback(HapticFeedbackType.LongPress)
                            trueSelected = !trueSelected
                            if (trueSelected) falseSelected = false
                        }
                    },
                    enabled = !state.showResult,
                    shape = RoundedCornerShape(12.dp),
                    colors = CardDefaults.outlinedCardColors(
                        containerColor = trueBg,
                        contentColor = MaterialTheme.colorScheme.onSurfaceVariant,
                        disabledContainerColor = trueBg,
                        disabledContentColor = MaterialTheme.colorScheme.onSurfaceVariant
                    ),
                    border = BorderStroke(
                        width = if (trueSelected && !state.showResult) 2.dp else 1.dp,
                        color = if (trueSelected && !state.showResult) {
                            MaterialTheme.colorScheme.primary
                        } else if (testMode && quiz.correct) {
                            testModeCorrect
                        } else {
                            MaterialTheme.colorScheme.outline.copy(alpha = 0.5f)
                        }
                    ),
                    modifier = Modifier
                        .padding(horizontal = 8.dp)
                        .fillMaxWidth()
                        .semantics { testTag = trueLabel }
                ) {
                    Box(
                        modifier = Modifier.fillMaxWidth()
                            .heightIn(min = if (deviceInfo.deviceClass == DeviceClass.PHONE_SMALL) 48.dp else 64.dp)
                            .padding(vertical = 8.dp),
                        contentAlignment = Alignment.Center
                    ) {
                        Text(
                            text = stringResource(Res.string.true_option),
                            style = MaterialTheme.typography.bodyLarge,
                            fontWeight = FontWeight.Bold,
                            textAlign = TextAlign.Center
                        )
                    }
                }

                OutlinedCard(
                    onClick = {
                        if (state.pending) {
                            hapticFeedback.performHapticFeedback(HapticFeedbackType.LongPress)
                            falseSelected = !falseSelected
                            if (falseSelected) trueSelected = false
                        }
                    },
                    enabled = !state.showResult,
                    shape = RoundedCornerShape(12.dp),
                    colors = CardDefaults.outlinedCardColors(
                        containerColor = falseBg,
                        contentColor = MaterialTheme.colorScheme.onSurfaceVariant,
                        disabledContainerColor = falseBg,
                        disabledContentColor = MaterialTheme.colorScheme.onSurfaceVariant
                    ),
                    border = BorderStroke(
                        width = if (falseSelected && !state.showResult) 2.dp else 1.dp,
                        color = if (falseSelected && !state.showResult) {
                            MaterialTheme.colorScheme.primary
                        } else if (testMode && !quiz.correct) {
                            testModeCorrect
                        } else {
                            MaterialTheme.colorScheme.outline.copy(alpha = 0.5f)
                        }
                    ),
                    modifier = Modifier
                        .padding(horizontal = 8.dp)
                        .fillMaxWidth()
                        .semantics { testTag = falseLabel }
                ) {
                    Box(
                        modifier = Modifier.fillMaxWidth()
                            .heightIn(min = if (deviceInfo.deviceClass == DeviceClass.PHONE_SMALL) 48.dp else 64.dp)
                            .padding(vertical = 8.dp),
                        contentAlignment = Alignment.Center
                    ) {
                        Text(
                            text = stringResource(Res.string.false_option),
                            style = MaterialTheme.typography.bodyLarge,
                            fontWeight = FontWeight.Bold,
                            textAlign = TextAlign.Center
                        )
                    }
                }
                }
            }

            QuizContentScaffold(
                testTag = "true_false_content",
                isLandscape = isLandscape,
                landscapeQuestionArrangement = Arrangement.Top,
                landscapeOptionsHorizontalAlignment = Alignment.CenterHorizontally,
                portraitOptionsArrangement = Arrangement.spacedBy(16.dp, Alignment.CenterVertically),
                portraitOptionsScrollable = false,
                portraitSpacerBeforeOptions = 24.dp,
                questionContent = { questionContent() },
                optionsContent = { buttonsContent() }
            )
        }
        val correctString =
            if (quiz.correct) stringResource(Res.string.true_option) else stringResource(Res.string.false_option)
        val quizInfo = """
            Quiz ID: ${quiz.id}
            Type: True/False Quiz
            Question: ${quiz.text.resolve()}
            Correct Answer: ${quiz.correct}
        """.trimIndent()
        val userAnswer = when {
            trueSelected -> stringResource(Res.string.true_option)
            falseSelected -> stringResource(Res.string.false_option)
            else -> "None"
        }

        val resolvedQuestion = quiz.text.resolve()
        val shareText = stringResource(Res.string.share_quiz_true_false, resolvedQuestion)

        QuizBottomFeedbackAndButton(
            modifier = Modifier.align(Alignment.BottomCenter),
            state = state,
            buttonEnabled = trueSelected || falseSelected,
            correctAnswer = correctString,
            userData = userData,
            lessonId = lessonId,
            quizInfo = quizInfo,
            userAnswer = userAnswer,
            shareText = shareText,
            shareImageProvider = null,
            onConfirmClick = {
                val isCorrect = trueSelected == quiz.correct
                onAnswerResult?.invoke(
                    isCorrect,
                    WrongAnswerRecord(
                        id = WrongAnswerRecord.buildId(lessonId, resolvedQuestion),
                        lessonId = lessonId,
                        type = QuizQuestionType.TRUE_FALSE,
                        questionText = resolvedQuestion,
                        // Canonical, locale-independent value (not the localized correctString)
                        // so a correction-mode rebuild of this record can recover the boolean
                        // reliably regardless of the app's current language.
                        correctAnswerText = quiz.correct.toString(),
                        userAnswerText = userAnswer
                    )
                )
                isCorrect
            }
        )
    }
}

@Composable
private fun RevealOptionsButton(onClick: () -> Unit) {
    val hapticFeedback = LocalHapticFeedback.current
    Box(
        modifier = Modifier.fillMaxWidth().padding(vertical = 24.dp),
        contentAlignment = Alignment.Center
    ) {
        Button(
            onClick = {
                hapticFeedback.performHapticFeedback(HapticFeedbackType.LongPress)
                onClick()
            }
        ) {
            Text(text = stringResource(Res.string.quiz_show_options_button))
        }
    }
}
